<template>
  <component
    :is="component"
    :class="buttonClasses"
    :disabled="disabled || loading"
    :to="to"
    :href="href"
    :type="type"
    @click="handleClick"
  >
    <!-- Loading Spinner -->
    <span v-if="loading" class="btn-spinner">
      <svg class="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" stroke-opacity="0.3"/>
        <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
      </svg>
    </span>
    
    <!-- Left Icon -->
    <span v-if="$slots.icon && !loading" class="btn-icon btn-icon--left">
      <slot name="icon"></slot>
    </span>
    
    <!-- Button Text -->
    <span v-if="$slots.default" class="btn-text">
      <slot></slot>
    </span>
    
    <!-- Right Icon -->
    <span v-if="$slots.iconRight" class="btn-icon btn-icon--right">
      <slot name="iconRight"></slot>
    </span>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline' | 'success' | 'warning' | 'danger'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  disabled?: boolean
  loading?: boolean
  block?: boolean
  iconOnly?: boolean
  to?: string
  href?: string
  type?: 'button' | 'submit' | 'reset'
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  disabled: false,
  loading: false,
  block: false,
  iconOnly: false,
  type: 'button'
})

const emit = defineEmits<{
  click: [event: Event]
}>()

const component = computed(() => {
  if (props.to) return 'router-link'
  if (props.href) return 'a'
  return 'button'
})

const buttonClasses = computed(() => {
  const classes = ['btn']
  
  // Variant
  classes.push(`btn--${props.variant}`)
  
  // Size
  if (props.size !== 'md') {
    classes.push(`btn--${props.size}`)
  }
  
  // Modifiers
  if (props.block) classes.push('btn--block')
  if (props.iconOnly) classes.push('btn--icon')
  if (props.loading) classes.push('btn--loading')
  
  return classes
})

const handleClick = (event: Event) => {
  if (!props.disabled && !props.loading) {
    emit('click', event)
  }
}
</script>

<style scoped>
.btn--block {
  width: 100%;
}

.btn--loading {
  cursor: wait;
}

.btn-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-icon--left {
  margin-right: var(--sp-1);
}

.btn-icon--right {
  margin-left: var(--sp-1);
}

.btn-text {
  display: flex;
  align-items: center;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Icon-only button adjustments */
.btn--icon .btn-icon--left,
.btn--icon .btn-icon--right {
  margin: 0;
}
</style>




